import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Users, CalendarDays, User, Menu, X, Sun, Moon, CalendarHeart, Sparkles, Wallet, User2, Receipt, Music, Disc3 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import NotificationBanner from "./NotificationBanner";

const NAV = [
  { to: "/calendar", label: "Calendar", testid: "nav-calendar", cal: true },
  { to: "/packages", label: "Buy Passes", testid: "nav-packages", passes: true },
  { center: true },
  { to: "/friends", label: "Friends", testid: "nav-friends", friends: true },
  { to: "/profile", label: "Profile", testid: "nav-profile", profile: true },
];

const MENU = [
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/friends", label: "Friends", icon: Users },
  { to: "/orders", label: "Orders", icon: Receipt },
  { to: "/packages", label: "Buy Passes", icon: Sparkles },
  { to: "/profile", label: "Profile", icon: User2 },
  { to: "/team", label: "Team", icon: User },
  { to: "/wallet", label: "Wallet", icon: Wallet },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const menuItems = React.useMemo(() => {
    const items = [...MENU];
    if (["dj", "admin", "super_admin"].includes(user?.role)) items.push({ to: "/dj-board", label: "DJ Board", icon: Disc3 });
    return items;
  }, [user?.role]);
  const [chatUnread, setChatUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  useEffect(() => { document.documentElement.classList.toggle("light", theme === "light"); }, [theme]);
  useEffect(() => {
    const h = (e) => { if (e.target.closest("[data-sonner-toast]")) toast.dismiss(); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);
  const setThemeMode = (next) => { localStorage.setItem("theme", next); setTheme(next); };

  const refetch = useCallback(() => api.get("/messages/unread-count").then((r) => setChatUnread(r.data.count || 0)).catch(() => {}), []);
  useEffect(() => { refetch(); }, [refetch, location.pathname]);
  useEffect(() => { const t = setInterval(refetch, 25000); return () => clearInterval(t); }, [refetch]);
  useEffect(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let alive = true; let ws;
    const connect = () => {
      ws = new WebSocket(`${base}/api/ws/chat`);
      ws.onmessage = (ev) => { try { if (JSON.parse(ev.data).event === "message") refetch(); } catch { /* noop */ } };
      ws.onclose = (e) => { if (alive && e.code !== 1008) setTimeout(connect, 4000); };
    };
    connect();
    return () => { alive = false; try { ws && ws.close(); } catch { /* noop */ } };
  }, [refetch]);

  // PWA app-icon badge — persists on the installed icon even after the app is closed
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    try { chatUnread > 0 ? navigator.setAppBadge(chatUnread) : navigator.clearAppBadge(); } catch { /* noop */ }
  }, [chatUnread]);

  if (user?.role === "bartender") return <Navigate to="/drinks" replace />;
  if (user?.role === "staff") return <Navigate to="/admin/scanner" replace />;

  return (
    <div className="min-h-[100dvh] w-full max-w-md sm:max-w-xl lg:max-w-3xl mx-auto relative bg-[#0D0B14]">
      <NotificationBanner />
      <div className="pb-nav min-h-[100dvh]">
        <Outlet />
      </div>

      <nav
        data-testid="bottom-nav"
        className="fixed bottom-[max(env(safe-area-inset-bottom),0.9rem)] left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.5rem)] max-w-md"
      >
        <div className="flex items-center rounded-[26px] bg-[#0B0B0F]/90 backdrop-blur-2xl border border-white/10 shadow-2xl shadow-black/60 px-2 py-2.5">
          {NAV.map((n) => {
            if (n.center) {
              return (
                <div key="home" className="flex-1 flex justify-center">
                  <NavLink to="/" data-testid="nav-home" aria-label="Home"
                    className="-mt-9 w-[66px] h-[66px] rounded-full bg-black flex items-center justify-center active:scale-95 transition-transform shrink-0 overflow-hidden"
                    style={{ boxShadow: "0 0 22px rgba(255,195,0,0.45)" }}>
                    <img src="/home-icon.png" alt="Home" className="w-full h-full object-contain" />
                  </NavLink>
                </div>
              );
            }
            const active = location.pathname === n.to;
            const showBadge = n.to === "/friends" && chatUnread > 0;
            const Icon = n.icon;
            return (
              <div key={n.to} className="flex-1 flex justify-center">
                <NavLink to={n.to} data-testid={n.testid} aria-label={n.label} className="flex flex-col items-center gap-1 active:scale-90 transition-transform">
                  <span className="relative">
                    {n.passes ? (
                      <img src="/passes-icon.png" alt="" className="w-[30px] h-[30px] object-contain -my-1" />
                    ) : n.cal ? (
                      <img src="/calendar-icon.png" alt="" className="w-[30px] h-[30px] object-contain -my-1" />
                    ) : n.friends ? (
                      <img src="/friends-icon.png" alt="" className="w-[30px] h-[30px] object-contain -my-1" />
                    ) : n.profile ? (
                      <img src="/profile-icon.png" alt="" className="w-[30px] h-[30px] object-contain -my-1" />
                    ) : (
                      <Icon size={22} strokeWidth={active ? 2.4 : 1.9} style={{ color: active ? "var(--gold)" : "var(--purple)" }} />
                    )}
                    {showBadge && <span data-testid="nav-friends-badge" className="absolute -top-1.5 -right-2 min-w-[15px] h-4 px-1 rounded-full bg-[#EC4899] text-white text-[9px] font-bold flex items-center justify-center">{chatUnread > 9 ? "9+" : chatUnread}</span>}
                  </span>
                  {n.passes ? (
                    <span className="text-[10px] font-semibold leading-none whitespace-nowrap"><span className="text-white">Buy</span> <span style={{ color: "var(--gold)" }}>Passes</span></span>
                  ) : (
                    <span className="text-[10px]" style={{ color: active ? "var(--gold)" : "var(--text-2)" }}>{n.label}</span>
                  )}
                </NavLink>
              </div>
            );
          })}
        </div>
      </nav>

      {menuOpen && <div className="fixed inset-0 z-[59]" onClick={() => setMenuOpen(false)} />}
      <div className="fixed inset-x-0 top-0 z-[60] mx-auto w-full max-w-md sm:max-w-xl lg:max-w-3xl pointer-events-none">
        <button data-testid="global-menu-btn" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu"
          className="pointer-events-auto absolute top-[max(env(safe-area-inset-top),0.7rem)] right-3 w-10 h-10 rounded-full bg-[#1a1424]/90 backdrop-blur border border-white/10 flex items-center justify-center active:scale-90 transition-transform">
          {menuOpen ? <X size={20} className="text-[#D4AF37]" /> : <Menu size={20} className="text-[#D4AF37]" />}
        </button>
        {menuOpen && (
          <div data-testid="global-menu" className="pointer-events-auto absolute top-[max(env(safe-area-inset-top),3.4rem)] right-3 z-[61] w-52 rounded-2xl bg-[#1a1424] border border-white/10 shadow-xl shadow-black/50 overflow-hidden">
            {menuItems.map((it) => { const Icon = it.icon; return (
              <button key={it.to} data-testid={`menu-${it.label.toLowerCase()}`} onClick={() => { navigate(it.to); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-white/5 active:bg-white/10">
                <Icon size={16} className="text-[#D4AF37]" /> {it.label}
              </button>
            ); })}
            <div className="border-t border-white/10 p-2" data-testid="menu-theme-toggle">
              <div className="flex items-center gap-1 rounded-full bg-white/5 p-1">
                <button data-testid="menu-theme-light" onClick={() => setThemeMode("light")}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-semibold transition-colors ${theme === "light" ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>
                  <Sun size={14} /> Light
                </button>
                <button data-testid="menu-theme-dark" onClick={() => setThemeMode("dark")}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-full py-1.5 text-xs font-semibold transition-colors ${theme === "dark" ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>
                  <Moon size={14} /> Dark
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
